import { TitleSlots } from './ai-optimize-utils';
import { AuditedProduct } from './scoring-engine-constants';
import {
  HighlightStatus,
  HighlightStatusType,
  ITEM_HIGHLIGHT_CHAR_LIMIT,
  TITLE_CHAR_LIMIT,
  TitleStatus,
  TitleStatusType
} from './title-compliance-constants';

/** The parser uses " — " (and occasionally an empty string) as the sentinel for a missing field. */
export const isMissingTitle = (title: string | null | undefined): boolean => {
  if (!title) return true;
  const t = title.trim();
  return t === '' || t === '—';
};

export interface TitleClassification {
  status: TitleStatusType;
  length: number;
  overBy: number;
}

export const classifyTitle = (title: string): TitleClassification => {
  if (isMissingTitle(title)) {
    return { status: TitleStatus.MISSING, length: 0, overBy: 0 };
  }
  const { length } = title;
  if (length > TITLE_CHAR_LIMIT) {
    return { status: TitleStatus.NEEDS_UPDATE, length, overBy: length - TITLE_CHAR_LIMIT };
  }
  return { status: TitleStatus.COMPLIANT, length, overBy: 0 };
};

export interface HighlightClassification {
  status: HighlightStatusType;
  length: number;
  overBy: number;
}

/**
 * Classify generated item-highlight text against the 125-char cap. The highlight
 * is net-new (no "current" to over-cap), so we only ever classify text we
 * generated: "empty" (not generated yet), "compliant", or (defensively) "over".
 */
export const classifyHighlight = (text: string | null | undefined): HighlightClassification => {
  const t = (text ?? '').trim();
  if (t === '') return { status: HighlightStatus.EMPTY, length: 0, overBy: 0 };
  const { length } = t;
  if (length > ITEM_HIGHLIGHT_CHAR_LIMIT) {
    return { status: HighlightStatus.OVER, length, overBy: length - ITEM_HIGHLIGHT_CHAR_LIMIT };
  }
  return { status: HighlightStatus.COMPLIANT, length, overBy: 0 };
};

export interface TitleComplianceSummary {
  total: number;
  needsUpdate: number;
  compliant: number;
  missing: number;
  longestTitle: number;
}

export const summarizeTitleCompliance = (products: AuditedProduct[]): TitleComplianceSummary => {
  const summary: TitleComplianceSummary = {
    total: products.length,
    needsUpdate: 0,
    compliant: 0,
    missing: 0,
    longestTitle: 0
  };

  for (const p of products) {
    const { status, length } = classifyTitle(p.title);
    if (status === TitleStatus.NEEDS_UPDATE) {
      summary.needsUpdate++;
    } else if (status === TitleStatus.COMPLIANT) {
      summary.compliant++;
    } else {
      summary.missing++;
    }
    if (length > summary.longestTitle) {
      summary.longestTitle = length;
    }
  }

  return summary;
};

// ── Saved title rewrites (separate slim store from proposals — a title
// rewrite is a single already-validated ≤75-char string per product) ──

const TITLE_REWRITES_STORAGE_KEY = 'pdp-scorecard-title-rewrites-v1';

export interface SavedTitleRewrite {
  /**
   * Rewritten title, already guaranteed ≤ TITLE_CHAR_LIMIT when stored. May be
   * "" for a highlight-only record (a compliant title needs no rewrite but the
   * user may still want an item highlight). Hydration keys off a non-empty
   * title, so an empty string reads back as "no title rewrite".
   */
  title: string;
  reasoning: string;
  generatedAt: number;
  /**
   * Optional Brand→Product→Variant→Size slot breakdown of the rewrite. Added
   * alongside the v2 prompt; pre-v2 records lack it and read back as undefined
   * (no key bump — the drawer just skips the slot chips for them).
   */
  slots?: TitleSlots;
  /**
   * Banned characters the strip pass removed from this rewrite, for the
   * "corrected" panel. Same tolerated-optional treatment as `slots`: records
   * written before it existed read back as undefined and the panel just omits
   * that line, so no key bump is needed.
   */
  removedChars?: string[];
  /**
   * Optional item-highlight companion field (≤ ITEM_HIGHLIGHT_CHAR_LIMIT).
   * Pre-existing records lack it and read back as undefined — readers tolerate
   * the missing field, so no key bump / migration is needed.
   */
  highlight?: {
    value: string;
    reasoning: string;
    generatedAt: number;
  };
}

export type TitleRewriteStore = Record<string, SavedTitleRewrite>;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === 'object';

/**
 * Validate the required core of a saved rewrite. Optional `slots`/`highlight`
 * are tolerated when malformed (dropped on read) rather than failing the whole
 * record — a bad optional field should never discard a valid title rewrite.
 */
const isSavedTitleRewrite = (v: unknown): v is SavedTitleRewrite => {
  if (!isPlainObject(v)) return false;
  return (
    typeof v.title === 'string' &&
    typeof v.reasoning === 'string' &&
    typeof v.generatedAt === 'number'
  );
};

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every(item => typeof item === 'string');

const isValidSlots = (v: unknown): v is TitleSlots =>
  isPlainObject(v) &&
  typeof v.brand === 'string' &&
  typeof v.product === 'string' &&
  typeof v.variant === 'string' &&
  typeof v.size === 'string';

const isValidHighlight = (v: unknown): v is SavedTitleRewrite['highlight'] =>
  isPlainObject(v) &&
  typeof v.value === 'string' &&
  typeof v.reasoning === 'string' &&
  typeof v.generatedAt === 'number';

/** Keep only the well-formed optional fields so bad data never reaches the UI. */
const sanitizeSavedRewrite = (r: SavedTitleRewrite): SavedTitleRewrite => {
  const rewrite: SavedTitleRewrite = {
    title: r.title,
    reasoning: r.reasoning,
    generatedAt: r.generatedAt
  };
  if (isValidSlots(r.slots)) {
    rewrite.slots = r.slots;
  }
  if (isStringArray(r.removedChars)) {
    rewrite.removedChars = r.removedChars;
  }
  if (isValidHighlight(r.highlight)) {
    rewrite.highlight = r.highlight;
  }
  return rewrite;
};

export const readTitleRewriteStore = (): TitleRewriteStore => {
  try {
    const raw = localStorage.getItem(TITLE_REWRITES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const next: TitleRewriteStore = {};
    for (const [id, rewrite] of Object.entries(parsed as Record<string, unknown>)) {
      if (isSavedTitleRewrite(rewrite)) {
        next[id] = sanitizeSavedRewrite(rewrite);
      }
    }
    return next;
  } catch {
    return {};
  }
};

export const writeTitleRewriteStore = (store: TitleRewriteStore): void => {
  try {
    localStorage.setItem(TITLE_REWRITES_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota / disabled — non-fatal.
  }
};

export const clearTitleRewriteStore = (): void => {
  try {
    localStorage.removeItem(TITLE_REWRITES_STORAGE_KEY);
  } catch {
    // ignore
  }
};

// ── View helpers (search / highlight / error shaping) ──

/** Case-insensitive match across ASIN, brand, and title — powers the TC search field. */
export const matchesTitleQuery = (product: AuditedProduct, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    (product.asin ?? '').toLowerCase().includes(q) ||
    (product.brand ?? '').toLowerCase().includes(q) ||
    (product.title ?? '').toLowerCase().includes(q)
  );
};

/** The over-cap tail of a title (the part past the 75-char limit), for red highlighting. */
export const overCapTail = (title: string): string =>
  title.length > TITLE_CHAR_LIMIT ? title.slice(TITLE_CHAR_LIMIT) : '';

/**
 * Turn an AI-call failure into a short, human-readable message. The CommerceOps
 * proxy surfaces backend crashes as the raw API-Gateway body
 * `{"message":"Internal server error","path":"N/A","params":"N/A"}` — never show
 * that to the user; map it (and other opaque errors) to something actionable.
 */
const rawErrorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return '';
};

/**
 * Spec tokens — measurements, capacities and counts — that frequently ARE the
 * only thing separating one variant listing from its siblings: a 250ml bottle
 * from a 120ml one, a 6" dodgeball from a 7", a 6" inseam from a 4".
 *
 * Deliberately narrow. It matches a number bound to a unit, not bare numbers,
 * so model numbers and years don't trip it.
 *
 * Alternation order is load-bearing: the longer spelling of an overlapping pair
 * MUST come first, or the shorter one matches its prefix and truncates the token
 * ("fl oz" before "oz", "inches" before "in", "ounce" before "oz").
 */
const SPEC_TOKEN_RE =
  /\b\d+(?:\.\d+)?\s*(?:"|''|-?inch(?:es)?|-?in\b|millimet(?:er|re)s?|mm|centimet(?:er|re)s?|cm|feet|foot|ft|quarts?|qt|gallons?|gal|fl\s*oz|fluid\s*ounces?|ounces?|oz|millilit(?:er|re)s?|ml|lit(?:er|re)s?|l\b|kilograms?|kg|grams?|g\b|pounds?|lbs?|counts?|ct\b|cnt|packs?|pk\b|pieces?|pcs?\b)/gi;

/**
 * Unit spellings that mean the same thing, mapped to one canonical form.
 *
 * Without this, `60 Count` → `60ct` reads as a DROPPED spec — a pure formatting
 * change reported as data loss. It hit supplements on essentially every row
 * (pack count is always present and always reformatted), which is exactly the
 * kind of false positive that trains reviewers to ignore the warning.
 *
 * Genuinely distinct units are deliberately NOT merged: `oz` (weight) stays
 * separate from `floz` (volume), and `l` from `ml`, because conflating those
 * would hide a real substitution.
 */
const UNIT_ALIASES: Record<string, string> = {
  // length
  '"': 'in',
  "''": 'in',
  in: 'in',
  inch: 'in',
  inches: 'in',
  mm: 'mm',
  millimeter: 'mm',
  millimeters: 'mm',
  millimetre: 'mm',
  millimetres: 'mm',
  cm: 'cm',
  centimeter: 'cm',
  centimeters: 'cm',
  centimetre: 'cm',
  centimetres: 'cm',
  ft: 'ft',
  foot: 'ft',
  feet: 'ft',
  // volume
  ml: 'ml',
  milliliter: 'ml',
  milliliters: 'ml',
  millilitre: 'ml',
  millilitres: 'ml',
  l: 'l',
  liter: 'l',
  liters: 'l',
  litre: 'l',
  litres: 'l',
  qt: 'qt',
  quart: 'qt',
  quarts: 'qt',
  gal: 'gal',
  gallon: 'gal',
  gallons: 'gal',
  floz: 'floz',
  fluidounce: 'floz',
  fluidounces: 'floz',
  // weight
  oz: 'oz',
  ounce: 'oz',
  ounces: 'oz',
  g: 'g',
  gram: 'g',
  grams: 'g',
  kg: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  // counts
  ct: 'ct',
  cnt: 'ct',
  count: 'ct',
  counts: 'ct',
  pk: 'pk',
  pack: 'pk',
  packs: 'pk',
  pc: 'pc',
  pcs: 'pc',
  piece: 'pc',
  pieces: 'pc'
};

/**
 * Collapse one matched spec token to `<number><canonical unit>` so the same
 * measurement compares equal however it was written: `2.30 Ounce`, `2.3 oz` and
 * `2.3oz` all become `2.3oz`.
 */
const canonicalSpecToken = (raw: string): string => {
  const compact = raw.toLowerCase().replace(/[\s-]+/g, '');
  const parsed = /^(\d+(?:\.\d+)?)(.*)$/.exec(compact);
  if (!parsed) return compact;
  const [, numeric, rawUnit] = parsed;
  // Number.parseFloat drops trailing zeros and leading padding, so 2.30 === 2.3.
  const value = Number.parseFloat(numeric);
  const number = Number.isFinite(value) ? String(value) : numeric;
  const unit = UNIT_ALIASES[rawUnit] ?? rawUnit;
  return `${number}${unit}`;
};

/**
 * Spec tokens present in a string, keyed by canonical form and valued by the
 * spelling the text actually used.
 *
 * The two are kept apart on purpose: comparison must be canonical (so `60 Count`
 * and `60ct` match), but what we show a reviewer must be the spelling from THEIR
 * title — reporting a lost `6"` inseam as `6in` would read as a different value
 * than the one they are looking at.
 */
const specTokens = (text: string): Map<string, string> => {
  const found = new Map<string, string>();
  for (const raw of text.match(SPEC_TOKEN_RE) ?? []) {
    const key = canonicalSpecToken(raw);
    if (!found.has(key)) {
      found.set(key, raw.trim());
    }
  }
  return found;
};

/**
 * Spec tokens that were in the ORIGINAL title but are missing from the rewrite.
 *
 * Measured across the reference sets, ~16% of rewrites silently drop one of
 * these — Dr Brown's bottle capacity, Champion dodgeball diameter, Honest Co
 * pack count, IZOD inseam. Each of those is the SKU's distinguishing attribute,
 * so losing it can collapse sibling variants into identical titles.
 *
 * This is a DETECTOR, not a fixer. Attempts to stop the model dropping them via
 * prompt instruction made the behaviour measurably worse, so instead of hiding
 * a probabilistic failure we surface it: the reviewer applying the title sees
 * exactly what was lost. Deterministic, category-agnostic, and it cannot regress
 * the cases that already pass.
 *
 * `highlight` is optional — when the item highlight carries the value, the fact
 * still reaches the PDP, so the caller can present it as informational rather
 * than as a loss.
 */
export interface DroppedSpecs {
  /** Tokens missing from the rewritten title. Empty when nothing was lost. */
  dropped: string[];
  /** Of those, the ones the item highlight still states. */
  recoveredInHighlight: string[];
}

export const findDroppedSpecs = (
  originalTitle: string,
  rewrittenTitle: string,
  highlight?: string
): DroppedSpecs => {
  if (!originalTitle || !rewrittenTitle) {
    return { dropped: [], recoveredInHighlight: [] };
  }
  const before = specTokens(originalTitle);
  const after = specTokens(rewrittenTitle);
  const droppedKeys = [...before.keys()].filter(key => !after.has(key));
  if (droppedKeys.length === 0) {
    return { dropped: [], recoveredInHighlight: [] };
  }
  const inHighlight = highlight ? specTokens(highlight) : new Map<string, string>();
  // Reported in the original title's own spelling — see `specTokens`.
  const display = (key: string): string => before.get(key) ?? key;
  return {
    dropped: droppedKeys.map(display),
    recoveredInHighlight: droppedKeys.filter(key => inHighlight.has(key)).map(display)
  };
};

// ── What the rewrite corrected, and where it broke its own rules ──
//
// Both of the following are deterministic diffs over (original, rewrite). They
// are DETECTORS, deliberately: the measured lesson from this change set is that
// prompt additions aimed at forcing a behaviour regressed it, while surfacing
// the outcome to the reviewer costs nothing and cannot regress a passing case.

/** Words that occur in both titles but differ, e.g. a casing or truncation fix. */
export interface TitleWordChange {
  /** The spelling the scraped title used. */
  from: string;
  /** The spelling the rewrite used. */
  to: string;
}

/**
 * Split a title into comparable words: whitespace-delimited, with surrounding
 * punctuation trimmed but internal punctuation kept, so `Dr.` and `Men's` stay
 * intact while `Gomacro,` compares as `Gomacro`.
 */
const titleWords = (text: string): string[] =>
  text
    .split(/\s+/)
    .map(w => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
    .filter(Boolean);

/** First-seen spelling for each lowercased word. */
const wordIndex = (words: string[]): Map<string, string> => {
  const index = new Map<string, string>();
  for (const word of words) {
    const key = word.toLowerCase();
    if (!index.has(key)) {
      index.set(key, word);
    }
  }
  return index;
};

/** Commas and semicolons — the punctuation scraped titles routinely scramble. */
const separatorCount = (text: string): number => (text.match(/[,;]/g) ?? []).length;

/**
 * A scraped title needs at least this many commas/semicolons before a reduction
 * is worth reporting as a tidy-up. Below it, a lower count in the rewrite is
 * more likely a side effect of dropping words to fit the cap than a correction,
 * and reporting that would be noise.
 */
const SCRAMBLED_SEPARATOR_FLOOR = 3;

export interface TitleCorrections {
  /** Words the rewrite re-capitalised — `Gomacro` → `GoMacro`. */
  capitalization: TitleWordChange[];
  /** Characters removed because Amazon suppresses them in titles. */
  removedChars: string[];
  /** Separator counts, present only when the rewrite measurably simplified them. */
  separators: { before: number; after: number } | null;
}

/**
 * What the tool quietly fixed on the way to a compliant title.
 *
 * Jen Mohan asked for this against B015OA6C0C, whose scraped title read
 * `Gomacro, Bar Protein Purity Sunflower Butter Chocolate Organic, 12 Count,
 * 2.3 Ounce…` — lowercase-m brand, scrambled commas. The tool corrected both
 * and said nothing, so the correction read as a coincidence rather than as work
 * the tool had done.
 *
 * Every field here is a stated fact about the two strings rather than a judgement
 * about what was wrong, which is what keeps it free of false positives:
 * `removedChars` comes from the strip pass itself, and the other two are counted
 * directly off the input.
 *
 * `removedChars` arrives from `optimizeTitleForCap`; it is not recoverable from
 * the two titles alone, since a banned character that was dropped along with its
 * whole word is not a character-level correction.
 */
export const findTitleCorrections = (
  originalTitle: string,
  rewrittenTitle: string,
  removedChars: string[] = []
): TitleCorrections => {
  const empty: TitleCorrections = {
    capitalization: [],
    removedChars: [...removedChars],
    separators: null
  };
  if (!originalTitle || !rewrittenTitle) {
    return empty;
  }

  const before = wordIndex(titleWords(originalTitle));
  const capitalization: TitleWordChange[] = [];
  for (const [key, after] of wordIndex(titleWords(rewrittenTitle))) {
    const original = before.get(key);
    if (original && original !== after) {
      capitalization.push({ from: original, to: after });
    }
  }

  const separatorsBefore = separatorCount(originalTitle);
  const separatorsAfter = separatorCount(rewrittenTitle);
  const separators =
    separatorsBefore >= SCRAMBLED_SEPARATOR_FLOOR && separatorsAfter < separatorsBefore
      ? { before: separatorsBefore, after: separatorsAfter }
      : null;

  return { ...empty, capitalization, separators };
};

/**
 * Line, collection and series names the v6 prompt guard protects (PR #1349).
 *
 * Listed here only to keep the abbreviation detector below off them: `Pro` is a
 * real line name AND a prefix of `Promaster`, so a rewrite that correctly keeps
 * `Pro` would otherwise be reported as having truncated it.
 */
const PROTECTED_LINE_NAMES = new Set([
  'performance',
  'stretch',
  'pinpoint',
  'advantage',
  'pro',
  'plus',
  'one',
  'weekender',
  'promaster',
  'superbreak'
]);

/**
 * Shortest rewrite word the abbreviation check will consider. Below this, prefix
 * matching is dominated by ordinary English (`Men` inside `Mens`, `Bar` inside
 * `Barrel`) rather than by truncation.
 */
const MIN_ABBREVIATION_LENGTH = 4;

/**
 * Smallest gap between the source word and the rewrite's shorter form. A
 * one-or-two-character difference is almost always an inflection the model
 * legitimately changed (`Flavors` → `Flavor`), not an abbreviation.
 */
const MIN_ABBREVIATION_GAP = 3;

/**
 * Nutrients, actives and ingredient callouts that Amazon supplement and beauty
 * titles routinely stuff. Used ONLY to check the consumer-goods formula's own
 * rule that these belong in the item highlight, never the title.
 *
 * `omega` matches with or without its number, because a rewrite that drops the
 * "3" has not stopped making an ingredient callout — B0DYBHFBH8's came back as
 * a bare "+ Omega".
 */
const INGREDIENT_CALLOUT =
  /\b(?:dha|epa|omega(?:[\s-]?3(?:-6-9)?)?|coq10|probiotics?|prebiotics?|collagen|biotin|melatonin|ashwagandha|turmeric|curcumin|magnesium|zinc|calcium|potassium|folate|folic\s+acid|niacinamide|retinol|hyaluronic\s+acid|salicylic\s+acid|glycolic\s+acid|ceramides?|peptides?|vitamins?\s*[a-k]\d?|b12|b6|d3|k2)\b/i;

/** Every occurrence, for scanning a whole title. */
const INGREDIENT_CALLOUT_RE = new RegExp(INGREDIENT_CALLOUT.source, 'gi');

/**
 * Whether a run of text ENDS in an active. Used to tell a compound product
 * name from a bolt-on — see `isBoltedOnIngredient`.
 */
const ENDS_WITH_INGREDIENT_RE = new RegExp(`${INGREDIENT_CALLOUT.source}\\s*$`, 'i');

/**
 * Word connectors that unambiguously mark what follows as an ADD-ON: "with DHA",
 * "plus Omega 3", "contains Collagen". Up to two words may sit in between, for
 * "with added DHA".
 */
const INGREDIENT_ADDON_WORD_RE =
  /(?:\bwith\b|\bplus\b|\bcontains\b|\benriched\b|\bfortified\b)\s+(?:[\w-]+\s+){0,2}$/i;

/** An `&` / `+` join, matched so that `.index` gives the symbol's position. */
const INGREDIENT_SYMBOL_JOIN_RE = /[&+]\s*(?:[\w-]+\s+){0,2}$/;

/**
 * Whether an active found in a title was BOLTED ON, rather than being part of
 * the product's own name.
 *
 * This is what keeps the ingredient check precise. `Biotin Gummies` and
 * `Vitamin C Serum` are products named after their active — legitimate product
 * nouns, never flagged. `Multi + Omega Gummies with DHA` bolts the same words on
 * behind a connector, which is the banned pattern.
 *
 * `&` and `+` are the subtle case, and treating them as connectors on sight was
 * wrong (caught in review on PR #1679): a product genuinely named after TWO
 * co-actives — `Biotin & Collagen Gummies`, `Vitamin C & Hyaluronic Acid Serum`
 * — had its SECOND active flagged, because the text before it ends in "… & ".
 * So a symbol join counts as a bolt-on only when what precedes the symbol is NOT
 * itself an active, which is exactly what separates `Multi + Omega` from
 * `Biotin & Collagen`. Tested against the whole preceding run rather than the
 * single word before the symbol, so multi-word actives resolve too.
 */
const isBoltedOnIngredient = (preceding: string): boolean => {
  if (INGREDIENT_ADDON_WORD_RE.test(preceding)) {
    return true;
  }
  const join = INGREDIENT_SYMBOL_JOIN_RE.exec(preceding);
  if (!join) {
    return false;
  }
  return !ENDS_WITH_INGREDIENT_RE.test(preceding.slice(0, join.index));
};

export interface TitleRuleViolations {
  /** Source words the rewrite truncated — `Multivitamin` → `Multi`. */
  abbreviated: TitleWordChange[];
  /**
   * Ingredient callouts bolted onto the title behind a connector, which the
   * consumer-goods formula routes to the item highlight instead. Consumer-goods
   * categories only — elsewhere the rule does not apply.
   */
  ingredientCallouts: string[];
}

/**
 * Where a finished rewrite broke a rule the tool itself states.
 *
 * Both checks came out of B0DYBHFBH8 (SmartyPants Toddler, reported by Brandi
 * Rice), whose rewrite — `SmartyPants Toddler Multi + Omega Gummies with DHA -
 * 60 Count` — violated the consumer-goods formula twice with ingredient
 * callouts and abbreviated the product noun on top of that. The prompt side of
 * that fix is in `CATEGORY_MAP`; this is the deterministic backstop for the runs
 * where the model does it anyway.
 *
 * NOTE the abbreviation check only catches a single word truncated in place. A
 * dropped modifier (`Stainless Steel` → `Steel`) is indistinguishable from the
 * word-dropping the cap legitimately requires, so it is left to the prompt rule.
 */
export const findTitleRuleViolations = (
  originalTitle: string,
  rewrittenTitle: string,
  isConsumerGoods: boolean
): TitleRuleViolations => {
  if (!originalTitle || !rewrittenTitle) {
    return { abbreviated: [], ingredientCallouts: [] };
  }

  const sourceWords = titleWords(originalTitle);
  const sourceKeys = new Set(sourceWords.map(w => w.toLowerCase()));
  const abbreviated: TitleWordChange[] = [];
  const seen = new Set<string>();

  for (const word of titleWords(rewrittenTitle)) {
    const key = word.toLowerCase();
    if (
      word.length < MIN_ABBREVIATION_LENGTH ||
      seen.has(key) ||
      sourceKeys.has(key) ||
      PROTECTED_LINE_NAMES.has(key)
    ) {
      continue;
    }
    const full = sourceWords.find(
      candidate =>
        candidate.toLowerCase().startsWith(key) &&
        candidate.length - word.length >= MIN_ABBREVIATION_GAP
    );
    if (full) {
      seen.add(key);
      abbreviated.push({ from: full, to: word });
    }
  }

  const ingredientCallouts: string[] = [];
  if (isConsumerGoods) {
    for (const match of rewrittenTitle.matchAll(INGREDIENT_CALLOUT_RE)) {
      const preceding = rewrittenTitle.slice(0, match.index);
      if (isBoltedOnIngredient(preceding) && !ingredientCallouts.includes(match[0])) {
        ingredientCallouts.push(match[0]);
      }
    }
  }

  return { abbreviated, ingredientCallouts };
};

export const formatAiError = (err: unknown): string => {
  const msg = rawErrorMessage(err);
  if (!msg) return 'Something went wrong generating this. Please try again.';
  if (/internal server error/i.test(msg) || /"message"\s*:/.test(msg)) {
    return 'The AI service is temporarily unavailable. Please try again in a moment.';
  }
  if (/api[\s_-]?key/i.test(msg)) {
    return 'AI is not configured for this environment.';
  }
  if (/missing (title|highlight) field/i.test(msg)) {
    return 'The AI returned an unexpected response. Please try again.';
  }
  // Fallback: show a short message, but never dump a JSON blob at the user.
  return msg.length > 140 || msg.trim().startsWith('{')
    ? 'Something went wrong generating this. Please try again.'
    : msg;
};
